import React, { useState, useEffect } from "react";
import {
  CopyOutlined,
  EditOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import moment from "moment";
import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import {
  calculatePricingAndLink,
  createOneToOneItem,
  fetchOneToOneItems,
  lookupCreator,
  updateOneToOneItem,
} from "../api";

const { Text } = Typography;

const globalPlans = [
  {
    key: 1,
    name: "Free",
    value: "free",
    platform: "tagmango",
    tier: "tier1",
  },
  {
    key: 2,
    name: "Freedom Basic",
    value: "freedom",
    platform: "freedom",
    tier: "tier1",
  },
  {
    key: 3,
    name: "Freedom Pro",
    value: "freedom",
    platform: "freedom",
    tier: "tier2",
  },
  {
    key: 4,
    name: "Freedom Advanced",
    value: "freedom",
    platform: "freedom",
    tier: "tier3",
  },
  {
    key: 5,
    name: "Enterprise Basic",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier1",
  },
  {
    key: 6,
    name: "Enterprise Pro",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier2",
  },
  {
    key: 7,
    name: "Enterprise Advanced",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier3",
  },
  {
    key: 8,
    name: "Enterprise Ultimate",
    value: "enterprise",
    platform: "enterprise",
    tier: "tier4",
  },
];

const getPlanKeyFromConfig = (platformSetup, tier) => {
  const plan = globalPlans.find(
    (p) => p.platform === platformSetup && p.tier === tier
  );
  return plan?.key || null;
};

const CreatorList = () => {
  const [loader, setLoader] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);
  const [isLifetimeValidity, setIsLifetimeValidity] = useState(false);
  const [fromPlan, setFromPlan] = useState(null);
  const [toPlan, setToPlan] = useState(null);
  const [calculatedBasePrice, setCalculatedBasePrice] = useState(null);
  const [calculatedMangoLink, setCalculatedMangoLink] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalValues, setOriginalValues] = useState(null);
  const [showNoMangoModal, setShowNoMangoModal] = useState(false);
  const [isCreateMangoDeleted, setIsCreateMangoDeleted] = useState(false);
  const [form] = Form.useForm();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filter modal states
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPlan, setFilterPlan] = useState([]);
  const [filterTier, setFilterTier] = useState([]);
  const [filterForm] = Form.useForm();

  // Filter options - using API parameter values
  const statusOptions = [
    { label: "Open", value: "open" },
    { label: "Converted", value: "converted" },
    { label: "Expired", value: "expired" },
  ];

  const planOptions = [
    { label: "TagMango (Free)", value: "tagmango" },
    { label: "Freedom", value: "freedom" },
    { label: "Enterprise", value: "enterprise" },
  ];

  const tierOptions = [
    { label: "Tier 1 (Basic)", value: "tier1" },
    { label: "Tier 2 (Pro)", value: "tier2" },
    { label: "Tier 3 (Advanced)", value: "tier3" },
    { label: "Tier 4 (Ultimate)", value: "tier4" },
  ];

  // Data from API
  const [creatorsData, setCreatorsData] = useState([]);

  // Map API response to table format
  const mapApiItemToTableRow = (item) => {
    const getPlanDisplayName = (platformSetup) => {
      if (platformSetup === "tagmango") return "TagMango";
      if (platformSetup === "freedom") return "Freedom";
      if (platformSetup === "enterprise") return "Enterprise";
      return platformSetup;
    };

    const getTierDisplayName = (tier) => {
      if (tier === "tier1") return "Basic";
      if (tier === "tier2") return "Pro";
      if (tier === "tier3") return "Advanced";
      if (tier === "tier4") return "Ultimate";
      return tier;
    };

    const getStatusDisplay = (status) => {
      if (status === "open") return "Open";
      if (status === "converted") return "Converted";
      if (status === "expired") return "Expired";
      return status;
    };

    // Build FROM plan name
    const fromPlanName =
      item.fromPlatformSetup === "tagmango"
        ? "Free"
        : `${getPlanDisplayName(item.fromPlatformSetup)} ${getTierDisplayName(
            item.fromTier
          )}`;

    // Build TO plan name
    const toPlanName =
      item.toPlatformSetup === "tagmango"
        ? "Free"
        : `${getPlanDisplayName(item.toPlatformSetup)} ${getTierDisplayName(
            item.toTier
          )}`;

    return {
      key: item._id,
      _id: item._id,
      name: item.creator?.name || "-",
      contact: item.creator?.phone
        ? `${item.creator?.dialCode || ""} ${item.creator?.phone}`
        : "-",
      email: item.creator?.email || "-",
      host: item.host?.includes("tagmango") ? "TAGMANGO" : "WHITELABEL",
      hostLink: item.host || "",
      productSetup: getPlanDisplayName(item.toPlatformSetup),
      fromProductSetup: getPlanDisplayName(item.fromPlatformSetup),
      tier: getTierDisplayName(item.toTier),
      fromTier: getTierDisplayName(item.fromTier),
      fromTo: `${fromPlanName} → ${toPlanName}`,
      status: getStatusDisplay(item.status),
      createdBy: item.createdBy?.name || "-",
      tierExpiryDate: item.expiry
        ? moment(item.expiry).format("DD MMM YYYY")
        : "-",
      link: item.coupon?.code
        ? `${process.env.REACT_APP_TAGMANGO_CHECKOUT_URL}/${item.saleMangoConfig?.mango?._id}?couponcode=${item.coupon?.code}`
        : "",
      discount: item.discountInPercentage || 0,
      validityInDays: item.validityInDays || 0,
      onetimeFee: item.onetimeFee || 0,
      subscriptionFee: item.subscriptionFee || 0,
      couponCode: item.couponCode || "",
      rawData: item,
    };
  };

  // Fetch data from API
  const fetchCreatorsList = async (page = 1, filters = {}, search = "") => {
    setLoader(true);
    try {
      const params = {
        limit: pageSize,
        page,
        sortOrder: "desc",
      };

      // Add search
      if (search?.trim()) {
        params.search = search.trim();
      }

      // Add status filter (supports multiple values)
      if (filters.status?.length > 0) {
        params.status = filters.status;
      }

      // Add plan filter - toPlatformSetup (supports multiple values)
      if (filters.plan?.length > 0) {
        const platformSetup = filters.plan.filter((p) => p === "tagmango");
        if (platformSetup.length > 0) {
          params.fromPlatformSetup = platformSetup;
        }
        params.toPlatformSetup = filters.plan.filter((p) => p !== "tagmango");
      }

      // Add tier filter - toTier (supports multiple values)
      if (filters.tier?.length > 0) {
        params.toTier = filters.tier;
      }

      const response = await fetchOneToOneItems(params);

      if (response?.code === 200 && response?.result?.items) {
        const mappedData = response.result.items.map(mapApiItemToTableRow);
        setCreatorsData(mappedData);
        setTotalItems(response.result.totalItems || 0);
      } else {
        setCreatorsData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching creators list:", error);
      message.error("Failed to fetch creators list");
      setCreatorsData([]);
      setTotalItems(0);
    } finally {
      setLoader(false);
    }
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCreatorsList(
        currentPage,
        {
          status: filterStatus,
          plan: filterPlan,
          tier: filterTier,
        },
        searchQuery
      );
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, filterStatus, filterPlan, filterTier, searchQuery]);

  const handleRefresh = () => {
    fetchCreatorsList(
      currentPage,
      {
        status: filterStatus,
        plan: filterPlan,
        tier: filterTier,
      },
      searchQuery
    );
    setTimeout(() => {
      setLoader(false);
    }, 3000);
  };

  // Check if lifetime validity applies based on from and to plan
  // const checkLifetimeValidity = (fromPlanKey, toPlanKey) => {
  //   const fromPlanObj = globalPlans.find((p) => p.key === fromPlanKey);
  //   const toPlanObj = globalPlans.find((p) => p.key === toPlanKey);

  //   if (!fromPlanObj || !toPlanObj) return false;

  //   // Freedom Basic (key 2) to Enterprise Basic (key 5) is lifetime
  //   if (
  //     fromPlanObj.name === "Freedom Basic" &&
  //     toPlanObj.name === "Enterprise Basic"
  //   ) {
  //     return true;
  //   }

  //   return false;
  // };

  // Map whitelabelPlanType from API to platform
  const mapWhitelabelPlanTypeToPlatform = (whitelabelPlanType) => {
    if (!whitelabelPlanType) return "tagmango";
    const type = whitelabelPlanType.toLowerCase();
    if (type.includes("freedom")) return "freedom";
    if (type.includes("enterprise")) return "enterprise";
    return "tagmango";
  };

  const handleVerifyEmail = async () => {
    const host = form.getFieldValue("host");
    const email = form.getFieldValue("emailId");
    const domain = form.getFieldValue("domain");

    if (!host) {
      message.error("Please select host first");
      return;
    }

    if (host === "TAGMANGO" && !email) {
      message.error("Please enter email");
      return;
    }

    if (host === "WHITELABEL" && !domain) {
      message.error("Please enter domain");
      return;
    }

    setIsVerifying(true);
    try {
      // Build payload based on host type
      const payload = {
        host: host.toLowerCase(),
        ...(host === "TAGMANGO"
          ? { email, domain: "tagmango.com" }
          : { domain }),
      };

      const response = await lookupCreator(payload);

      if (!response?.success || !response?.result?.creator) {
        throw new Error("Creator details not found");
      }

      const { creator } = response.result;

      // Map whitelabelPlanType to platform (freedom/enterprise/tagmango)
      const platformSetup = mapWhitelabelPlanTypeToPlatform(
        creator.whitelabelPlanType
      );
      const tier = creator.userTier || "tier1";

      // Find the matching plan key from globalPlans
      const planKey = getPlanKeyFromConfig(platformSetup, tier);
      const planObj = globalPlans.find((p) => p.key === planKey);

      if (!planKey) {
        throw new Error("Unable to determine creator plan");
      }

      setVerifiedData({
        platformSetup,
        tier,
        currentPlanName: planObj?.name || "Unknown",
        currentPlanKey: planKey,
        creatorId: creator._id,
        creatorName: creator.name,
        creatorEmail: creator.email,
        creatorPhone: creator.phone,
        tierExpiry: creator.accountSettingTierSubscriptionExpiry,
      });
      setIsVerified(true);
      setFromPlan(planKey);

      // Set the fetched plan to form
      form.setFieldsValue({
        userTypeFrom: planKey,
        planTypeTo: null,
      });
      setToPlan(null);

      message.success("Creator verified successfully!");
    } catch (error) {
      console.error("Verification error:", error);
      message.error(error?.message || "Creator details not found");
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePlanTypeToChange = async (value) => {
    setToPlan(value);

    const fromPlanObj = globalPlans.find((p) => p.key === fromPlan);
    const toPlanObj = globalPlans.find((p) => p.key === value);

    if (!fromPlanObj || !toPlanObj) {
      return;
    }

    // Check for lifetime validity
    if (
      toPlanObj &&
      ["enterprise basic", "freedom basic"].includes(
        toPlanObj.name?.toLowerCase()?.trim()
      )
    ) {
      form.setFieldsValue({ validity: "Lifetime", subscriptionFee: null });
      setIsLifetimeValidity(true);
    } else {
      form.setFieldsValue({ validity: null });
      setIsLifetimeValidity(false);
    }

    // Fetch mango link and base price from API
    setCalculationLoading(true);
    try {
      const query = new URLSearchParams({
        fromPlatformSetup: fromPlanObj.platform,
        fromTier: fromPlanObj.tier,
        toPlatformSetup: toPlanObj.platform,
        toTier: toPlanObj.tier,
      }).toString();

      const response = await calculatePricingAndLink(query);
      if (
        response?.code === 200 &&
        response?.result &&
        response?.result?.mangoConfigs?.[0]
      ) {
        const mango = response.result?.mangoConfigs?.[0]?.mango;
        // If creating a new item with a deleted mango, block with modal
        if (mango?.isDeleted && !selectedCreator) {
          setIsCreateMangoDeleted(true);
          setShowNoMangoModal(true);
          setCalculatedBasePrice(0);
          setCalculatedMangoLink(null);
        } else {
          setIsCreateMangoDeleted(false);
          setCalculatedBasePrice(mango?.inrAmount);
          setCalculatedMangoLink(
            `${process.env.REACT_APP_TAGMANGO_CHECKOUT_URL}/${mango?._id}`
          );
        }
      } else {
        setCalculatedBasePrice(0);
        setCalculatedMangoLink(null);
      }
    } catch (error) {
      console.error("Calculation error:", error);
      message.error("Failed to fetch pricing details");
    } finally {
      setCalculationLoading(false);
    }
  };

  const handleConfigurePlan = async (creator = null) => {
    setSelectedCreator(creator);
    setIsModalOpen(true);
    setIsVerified(false);
    setVerifiedData(null);
    setIsLifetimeValidity(false);
    setFromPlan(null);
    setToPlan(null);
    setCalculatedBasePrice(null);
    setCalculatedMangoLink(null);
    setCalculationLoading(false);
    setOriginalValues(null);
    setIsCreateMangoDeleted(false);

    if (creator) {
      // For edit mode, get plan keys from rawData (original API values)
      const rawData = creator.rawData || {};

      // Get from plan key
      const fromPlatformSetup = rawData.fromPlatformSetup || "tagmango";
      const fromTierValue = rawData.fromTier || "tier1";
      const fromPlanKey = getPlanKeyFromConfig(
        fromPlatformSetup,
        fromTierValue
      );
      const fromPlanObj = globalPlans.find((p) => p.key === fromPlanKey);

      // Get to plan key
      const toPlatformSetup = rawData.toPlatformSetup || "freedom";
      const toTierValue = rawData.toTier || "tier1";
      const toPlanKey = getPlanKeyFromConfig(toPlatformSetup, toTierValue);
      const toPlanObj = globalPlans.find((p) => p.key === toPlanKey);

      // Check if lifetime validity applies
      const isLifetime =
        toPlanObj &&
        ["enterprise basic", "freedom basic"].includes(
          toPlanObj.name?.toLowerCase()?.trim()
        );

      // Get validity in days directly
      const validityInDaysValue = rawData.validityInDays || 0;

      // Store original values for comparison during update
      setOriginalValues({
        discountInPercentage: rawData.discountInPercentage || 0,
        validityInDays: validityInDaysValue,
        onetimeFee: rawData.onetimeFee || 0,
        subscriptionFee: rawData.subscriptionFee || 0,
        expiry: rawData.expiry || null,
        status: rawData.status || "open",
      });

      // Set states
      setIsVerified(true);
      setFromPlan(fromPlanKey);
      setToPlan(toPlanKey);
      setIsLifetimeValidity(isLifetime);

      // Set calculated price and link from rawData
      if (rawData.saleMangoConfig?.mango) {
        setCalculatedBasePrice(rawData.saleMangoConfig.mango.inrAmount);
        setCalculatedMangoLink(
          `${process.env.REACT_APP_TAGMANGO_CHECKOUT_URL}/${rawData.saleMangoConfig.mango._id}`
        );
      }

      setVerifiedData({
        platformSetup: fromPlatformSetup,
        tier: fromTierValue,
        currentPlanName: fromPlanObj?.name || "Unknown",
        currentPlanKey: fromPlanKey,
        creatorId: rawData.creator?._id || creator._id || creator.key,
        creatorName: rawData.creator?.name || creator.name,
        creatorEmail: rawData.creator?.email || creator.email,
      });

      // Determine host type
      const hostType = rawData.host?.includes("tagmango")
        ? "TAGMANGO"
        : "WHITELABEL";

      // Set form values
      form.setFieldsValue({
        host: hostType,
        emailId: rawData.creator?.email || creator.email,
        domain: hostType === "WHITELABEL" ? rawData.host : undefined,
        userTypeFrom: fromPlanKey,
        planTypeTo: toPlanKey,
        discount: rawData.discountInPercentage || 0,
        validity: isLifetime ? "Lifetime" : validityInDaysValue,
        oneTimeFee: rawData.onetimeFee?.toFixed(2) || 0,
        subscriptionFee: rawData.subscriptionFee?.toFixed(2) || 0,
        expiryDate: rawData.expiry ? moment(rawData.expiry) : null,
      });
    } else {
      form.resetFields();
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedCreator(null);
    setIsVerified(false);
    setVerifiedData(null);
    setIsLifetimeValidity(false);
    setFromPlan(null);
    setToPlan(null);
    setCalculatedBasePrice(null);
    setCalculatedMangoLink(null);
    setCalculationLoading(false);
    setIsSubmitting(false);
    setOriginalValues(null);
    setIsCreateMangoDeleted(false);
    form.resetFields();
  };

  const handleFormSubmit = async (values) => {
    console.log("Form values:", values);
    console.log("Selected creator:", selectedCreator);
    console.log("Verified data:", verifiedData);

    if (!selectedCreator && isCreateMangoDeleted) {
      setShowNoMangoModal(true);
      return;
    }

    const fromPlanObj = globalPlans.find((p) => p.key === fromPlan);
    const toPlanObj = globalPlans.find((p) => p.key === toPlan);

    if (!fromPlanObj || !toPlanObj) {
      message.error("Please select Plan Type (To)");
      return;
    }

    // Calculate discounted price and validate fees
    const basePrice = parseFloat(calculatedBasePrice) || 0;
    const discount = values.discount || 0;
    const discountedPrice = basePrice - (basePrice * discount) / 100;
    const subscriptionFeeValue = isLifetimeValidity
      ? 0
      : values.subscriptionFee || 0;
    const summationValue =
      Number(values.oneTimeFee || 0) + Number(subscriptionFeeValue);

    if (Math.round(summationValue) !== Math.round(discountedPrice)) {
      const errorMsg = isLifetimeValidity
        ? `One-time Fee should equal ₹${discountedPrice?.toFixed(2)}`
        : `One-time Fee + Subscription Fee should equal ₹${discountedPrice?.toFixed(
            2
          )}`;
      message.error(errorMsg);
      return;
    }

    // Check if creator is verified
    if (!verifiedData?.creatorId) {
      message.error("Please verify the creator first");
      return;
    }

    // Validate expiry date
    if (!values.expiryDate) {
      message.error("Please select expiry date");
      return;
    }

    // Get validity in days (0 for lifetime)
    const validityInDays =
      isLifetimeValidity || values.validity === "Lifetime"
        ? 0
        : parseInt(values.validity, 10) || 1;

    // Get expiry date from form and set time to end of day
    const expiryDate = moment(values.expiryDate).endOf("day").toISOString();

    try {
      setIsSubmitting(true);

      if (selectedCreator && originalValues) {
        // Edit mode - only send modified fields
        const currentValues = {
          discountInPercentage: values.discount || 0,
          validityInDays,
          onetimeFee: values.oneTimeFee || 0,
          subscriptionFee: values.subscriptionFee || 0,
          expiry: expiryDate,
        };

        // Compare and only include modified fields
        const modifiedPayload = {};

        if (
          currentValues.discountInPercentage !==
          originalValues.discountInPercentage
        ) {
          modifiedPayload.discountInPercentage =
            currentValues.discountInPercentage;
        }
        if (currentValues.validityInDays !== originalValues.validityInDays) {
          modifiedPayload.validityInDays = currentValues.validityInDays;
        }
        if (
          Number(currentValues.onetimeFee) !== Number(originalValues.onetimeFee)
        ) {
          modifiedPayload.onetimeFee = Number(currentValues.onetimeFee);
        }
        if (
          Number(currentValues.subscriptionFee) !==
          Number(originalValues.subscriptionFee)
        ) {
          modifiedPayload.subscriptionFee = Number(
            currentValues.subscriptionFee
          );
        }
        // Compare expiry dates
        const originalExpiry = originalValues.expiry
          ? moment(originalValues.expiry).endOf("day").toISOString()
          : null;
        if (currentValues.expiry !== originalExpiry) {
          modifiedPayload.expiry = currentValues.expiry;
        }

        console.log("Modified Payload:", modifiedPayload);

        // Check if any field was modified
        if (Object.keys(modifiedPayload).length === 0) {
          message.info("No changes detected");
          handleModalCancel();
          setIsSubmitting(false);
          return;
        }

        // Call update API
        const itemId = selectedCreator._id || selectedCreator.key;
        const response = await updateOneToOneItem(itemId, modifiedPayload);

        if (response?.success || response?.code === 200) {
          message.success("Creator plan updated successfully!");
          handleModalCancel();
          handleRefresh();
        } else {
          message.error(response?.message || "Failed to update configuration");
        }
      } else {
        // Create mode - send all fields
        const payload = {
          creator: verifiedData.creatorId,
          toPlatformSetup: toPlanObj.platform,
          toTier: toPlanObj.tier,
          discountInPercentage: values.discount || 0,
          validityInDays,
          onetimeFee: Number(values.oneTimeFee) || 0,
          subscriptionFee: Number(values.subscriptionFee) || 0,
          expiry: expiryDate,
        };

        console.log("Create Payload:", payload);

        const response = await createOneToOneItem(payload);

        if (response?.success || response?.code === 200) {
          message.success("Creator plan configured successfully!");
          handleModalCancel();
          handleRefresh();
        } else {
          message.error(response?.message || "Failed to save configuration");
        }
      }
    } catch (error) {
      console.error("Error saving 1:1 item:", error);
      message.error(error?.message || "Failed to save configuration");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMenuAction = (action, record) => {
    switch (action) {
      case "edit":
        handleConfigurePlan(record);
        break;
      default:
        break;
    }
  };

  const getActionMenuItems = (record) => [
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined style={{ color: "#7c3aed" }} />,
      onClick: () => handleMenuAction("edit", record),
    },
  ];

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text) => (
        <Typography.Text style={{ fontWeight: 500 }}>{text}</Typography.Text>
      ),
    },
    {
      title: "CONTACT",
      dataIndex: "contact",
      key: "contact",
      width: 280,
      render: (text, record) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Typography.Text>{text}</Typography.Text>
            {text && text !== "-" && (
              <Tooltip title="Copy Phone">
                <CopyOutlined
                  style={{ color: "#9ca3af", cursor: "pointer", fontSize: 12 }}
                  onClick={() => {
                    if (!text || text === "-") return;
                    navigator.clipboard.writeText(
                      text?.split(" ")?.[1]?.trim()
                    );
                    message.success("Phone copied!");
                  }}
                />
              </Tooltip>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Typography.Text>
            {record.email && record.email !== "-" && (
              <Tooltip title="Copy Email">
                <CopyOutlined
                  style={{ color: "#9ca3af", cursor: "pointer", fontSize: 12 }}
                  onClick={() => {
                    navigator.clipboard.writeText(record.email);
                    message.success("Email copied!");
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "LINK",
      dataIndex: "link",
      key: "link",
      width: 180,
      render: (link, record) => {
        const mango = record.rawData?.saleMangoConfig?.mango;
        const isMangoDeleted = mango?.isDeleted;
        const isMangoHidden = mango?.isHidden;
        const isDisabled = record.status === "Expired" || isMangoDeleted;
        return (
          <div>
            <p
              style={{
                color: "#7c3aed",
                cursor: isDisabled ? "not-allowed" : "pointer",
                margin: 0,
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={async () => {
                if (isDisabled) return;
                await navigator.clipboard.writeText(link);
                message.success("Link copied to clipboard!");
              }}
            >
              Copy&nbsp;Link
            </p>
            {(isMangoDeleted || isMangoHidden) && (
              <Typography.Text
                style={{
                  display: "block",
                  color: "#ef4444",
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {isMangoDeleted
                  ? "This Mango has been deleted."
                  : "This Mango is currently hidden in your account."}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: "HOST",
      dataIndex: "host",
      key: "host",
      width: 190,
      render: (text, record) => (
        <div>
          <Tag
            color={text === "TAGMANGO" ? "purple" : "blue"}
            bordered={false}
            style={{ marginBottom: record.hostLink ? 4 : 0 }}
          >
            {text}
          </Tag>
          {record.hostLink && (
            <>
              <br />
              <Typography.Link
                href={`https://${record.hostLink}`}
                target="_blank"
                style={{ fontSize: 12 }}
              >
                {record.hostLink}
              </Typography.Link>
            </>
          )}
        </div>
      ),
    },
    {
      title: "FROM → TO",
      dataIndex: "fromTo",
      key: "fromTo",
      width: 220,
      render: (_, record) => {
        return (
          <Typography.Text style={{ fontWeight: 500 }}>
            <p style={{ margin: 0 }}>{record.fromTo}</p>
            {!record.validityInDays && record?.rawData?.toTier === "tier1" ? (
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                validity : Lifetime
              </p>
            ) : (
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                validity : {record.validityInDays} days
              </p>
            )}
          </Typography.Text>
        );
      },
    },
    {
      title: "CREATED BY",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 180,
      render: (text) => (
        <div>
          <Typography.Text>{text || "-"}</Typography.Text>
          {/* {text && (
            <>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {record?.rawData?.createdBy?.email || "-"}
              </Typography.Text>
            </>
          )} */}
        </div>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => {
        let color = "default";
        if (status === "Converted" || record?.rawData?.isConverted) {
          color = "success";
        } else if (status === "Open") {
          color = "warning";
        } else if (status === "Expired") {
          color = "error";
        }
        return (
          <Tag color={color} bordered={false}>
            {record?.rawData?.isConverted ? "Converted" : status}
          </Tag>
        );
      },
    },
    {
      title: "TIER EXPIRY DATE",
      dataIndex: "tierExpiryDate",
      key: "tierExpiryDate",
      width: 150,
    },
    {
      title: "ACTION",
      dataIndex: "action",
      key: "action",
      width: 90,
      fixed: "right",
      render: (_, record) => {
        const isMangoDeleted =
          record.rawData?.saleMangoConfig?.mango?.isDeleted;
        return (
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={["click"]}
            placement="bottomRight"
            disabled={record?.rawData?.isConverted || isMangoDeleted}
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: 20 }} />}
              onClick={(e) => e.preventDefault()}
            />
          </Dropdown>
        );
      },
    },
  ];

  // Data is filtered by API, use directly
  const filteredData = creatorsData;

  // Handle filter modal
  const handleOpenFilterModal = () => {
    filterForm.setFieldsValue({
      status: filterStatus,
      plan: filterPlan,
      tier: filterTier,
    });
    setIsFilterModalOpen(true);
  };

  const handleFilterModalCancel = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (values) => {
    setCurrentPage(1);
    setFilterStatus(values.status || []);
    setFilterPlan(values.plan || []);
    setFilterTier(values.tier || []);
    setIsFilterModalOpen(false);
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    setFilterStatus([]);
    setFilterPlan([]);
    setFilterTier([]);
    filterForm.resetFields();
    setIsFilterModalOpen(false);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const activeFilterCount =
    filterStatus.length + filterPlan.length + filterTier.length;

  console.log({ setCreatorsData });
  return (
    <>
      <FiltersWrapper style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <CustomSearchbar
            placeholder="Search creators by name or host..."
            searchQuery={searchQuery}
            setSearchQuery={(value) => {
              setCurrentPage(1);
              setSearchQuery(value);
            }}
            style={{ width: 400 }}
          />
          <Tooltip title="Refresh">
            <Button size="large" onClick={handleRefresh}>
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Tooltip title="Filter">
            <Button
              size="large"
              onClick={handleOpenFilterModal}
              style={{
                position: "relative",
              }}
            >
              <FilterOutlined />
              {activeFilterCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    background: "#7c3aed",
                    color: "#fff",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    fontSize: 11,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </Tooltip>

          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => handleConfigurePlan()}
            style={{
              background: "#7c3aed",
              borderColor: "#7c3aed",
            }}
          >
            Configure Plan
          </Button>
        </div>
      </FiltersWrapper>

      <div
        style={{
          background: "#fff",
          padding: "14px",
          borderRadius: 8,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          marginTop: 24,
        }}
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loader}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalItems,
            onChange: handlePageChange,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          locale={{
            emptyText: "No creators found",
          }}
          scroll={{ x: 1400 }}
          rowKey="key"
        />
      </div>

      {/* Configure Creator Plan Modal */}
      <Modal
        title={selectedCreator ? "Edit Creator Plan" : "Configure Creator Plan"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={500}
        destroyOnClose
        centered
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingRight: 8,
          },
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <span style={{ fontWeight: 600, color: "#6b7280" }}>
                      HOST
                    </span>
                    <Tooltip title="Select the platform host type">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Space>
                }
                name="host"
                rules={[{ required: true, message: "Please select host!" }]}
                style={{ marginBottom: 24 }}
              >
                <Select
                  size="large"
                  placeholder="Select Host"
                  style={{ borderRadius: 8 }}
                  disabled={!!selectedCreator || isVerified}
                  onChange={() => {
                    form.setFieldsValue({
                      emailId: undefined,
                      domain: undefined,
                    });
                  }}
                >
                  <Select.Option value="TAGMANGO">TagMango</Select.Option>
                  <Select.Option value="WHITELABEL">WhiteLabel</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.host !== currentValues.host
                }
              >
                {({ getFieldValue }) => {
                  const host = getFieldValue("host");

                  if (host === "WHITELABEL") {
                    return (
                      <Form.Item
                        label={
                          <span style={{ fontWeight: 600, color: "#6b7280" }}>
                            DOMAIN
                          </span>
                        }
                        name="domain"
                        rules={[
                          { required: true, message: "Please input domain!" },
                        ]}
                        style={{ marginBottom: 8 }}
                      >
                        <Input
                          size="large"
                          placeholder="e.g. example.com"
                          style={{ borderRadius: 8 }}
                          disabled={!!selectedCreator || isVerified}
                        />
                      </Form.Item>
                    );
                  }

                  return (
                    <Form.Item
                      label={
                        <span style={{ fontWeight: 600, color: "#6b7280" }}>
                          EMAIL ID
                        </span>
                      }
                      name="emailId"
                      rules={[
                        { required: true, message: "Please input email!" },
                        { type: "email", message: "Please enter valid email!" },
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        size="large"
                        placeholder="e.g. amit@example.com"
                        style={{ borderRadius: 8 }}
                        disabled={!!selectedCreator || isVerified}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          {/* Verify Button */}
          {!selectedCreator && !isVerified && (
            <Form.Item style={{ marginBottom: 24 }}>
              <Button
                type="primary"
                onClick={handleVerifyEmail}
                loading={isVerifying}
                style={{
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                  borderRadius: 8,
                }}
              >
                {isVerifying ? "Verifying..." : "Verify"}
              </Button>
            </Form.Item>
          )}

          {/* Rest of the form - Only show after verification */}
          {isVerified && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 600, color: "#6b7280" }}>
                        USER TYPE (FROM)
                      </span>
                    }
                    name="userTypeFrom"
                    rules={[
                      { required: true, message: "Please select user type!" },
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    <Select
                      size="large"
                      placeholder="Select Plan"
                      style={{ borderRadius: 8 }}
                      disabled
                    >
                      {globalPlans.map((plan) => (
                        <Select.Option key={plan.key} value={plan.key}>
                          {plan.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 600, color: "#6b7280" }}>
                        PLAN TYPE (TO)
                      </span>
                    }
                    name="planTypeTo"
                    rules={[
                      { required: true, message: "Please select plan type!" },
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    <Select
                      size="large"
                      placeholder="Select Plan"
                      style={{ borderRadius: 8 }}
                      onChange={handlePlanTypeToChange}
                      disabled={!!selectedCreator}
                    >
                      {fromPlan ? (
                        globalPlans
                          .filter((plan) => plan.key > fromPlan)
                          .map((plan) => (
                            <Select.Option key={plan.key} value={plan.key}>
                              {plan.name}
                            </Select.Option>
                          ))
                      ) : (
                        <Select.Option value={null} disabled>
                          Choose User Type First
                        </Select.Option>
                      )}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Calculation Section - Shows when both fromPlan and toPlan are selected */}
              {fromPlan && toPlan && (
                <div
                  style={{
                    background: "#fff4e8",
                    border: "1px solid #f18926",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 24,
                  }}
                >
                  {calculationLoading ? (
                    <div style={{ textAlign: "center", padding: "8px 0" }}>
                      <Text style={{ color: "#f18926" }}>
                        Fetching pricing details...
                      </Text>
                    </div>
                  ) : (
                    <>
                      <Row
                        justify="space-between"
                        align="middle"
                        style={{ marginBottom: 8 }}
                      >
                        <Col>
                          <Text style={{ color: "#f18926", fontWeight: 500 }}>
                            Mango Link:
                          </Text>
                        </Col>
                        <Col>
                          <Space>
                            <Text
                              style={{
                                maxWidth: 180,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                                color: "#f18926",
                                marginTop: 6,
                              }}
                            >
                              {calculatedMangoLink || "Not Configured"}
                            </Text>
                            <Button
                              type="link"
                              size="small"
                              icon={<CopyOutlined />}
                              style={{ color: "#f18926", padding: 0 }}
                              disabled={!calculatedMangoLink}
                              onClick={() => {
                                if (calculatedMangoLink) {
                                  navigator.clipboard.writeText(
                                    calculatedMangoLink
                                  );
                                  message.success("Link copied to clipboard!");
                                }
                              }}
                            />
                          </Space>
                        </Col>
                      </Row>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text style={{ color: "#f18926", fontWeight: 500 }}>
                            Base Price:
                          </Text>
                        </Col>
                        <Col>
                          <Text style={{ color: "#f18926", fontWeight: 600 }}>
                            {calculatedBasePrice
                              ? `₹${calculatedBasePrice?.toFixed(2)}`
                              : "-"}
                          </Text>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 600, color: "#6b7280" }}>
                        DISCOUNT
                      </span>
                    }
                    name="discount"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.reject(
                              new Error("Please enter discount")
                            );
                          }
                          if (value > 100) {
                            return Promise.reject(
                              new Error("Discount cannot be greater than 100%")
                            );
                          }
                          if (
                            Number.isInteger(value) &&
                            value > 0 &&
                            value <= 100
                          ) {
                            return Promise.resolve();
                          }

                          if (value > 0) {
                            return Promise.reject(
                              new Error("No fractional numbers allowed")
                            );
                          }
                        },
                      },
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    <InputNumber
                      size="large"
                      placeholder="0"
                      min={0}
                      max={100}
                      style={{ width: "100%", borderRadius: 8 }}
                      addonAfter="%"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 600, color: "#6b7280" }}>
                        VALIDITY <small>(in days)</small>
                      </span>
                    }
                    name="validity"
                    rules={[
                      {
                        required: !isLifetimeValidity,
                        message: "Please enter validity in days!",
                      },
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    {isLifetimeValidity ? (
                      <Input
                        size="large"
                        placeholder="Lifetime"
                        value="Lifetime"
                        disabled
                        style={{ width: "100%", borderRadius: 8, padding: 8 }}
                      />
                    ) : (
                      <InputNumber
                        size="large"
                        placeholder="Enter days"
                        min={1}
                        style={{ width: "100%", borderRadius: 8 }}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              {/* Discounted Price Section - Shows when discount is entered */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.discount !== currentValues.discount ||
                  prevValues.userTypeFrom !== currentValues.userTypeFrom ||
                  prevValues.planTypeTo !== currentValues.planTypeTo
                }
              >
                {({ getFieldValue }) => {
                  const discount = getFieldValue("discount");

                  if (!discount || discount <= 0 || !fromPlan || !toPlan)
                    return null;

                  const basePrice = parseFloat(calculatedBasePrice) || 0;
                  const discountedPrice =
                    basePrice - (basePrice * discount) / 100;

                  return (
                    <div
                      style={{
                        background: "#ecfdf5",
                        border: "1px solid #10b981",
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 24,
                      }}
                    >
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Typography.Text
                            style={{ color: "#059669", fontWeight: 500 }}
                          >
                            Discounted Price:
                          </Typography.Text>
                        </Col>
                        <Col>
                          <Space>
                            <Typography.Text
                              style={{
                                color: "#9ca3af",
                                textDecoration: "line-through",
                                fontSize: 14,
                              }}
                            >
                              ₹{basePrice?.toFixed(2)}
                            </Typography.Text>
                            <Typography.Text
                              style={{
                                color: "#059669",
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              ₹{discountedPrice?.toFixed(2)}
                            </Typography.Text>
                            <Tag color="green" style={{ margin: 0 }}>
                              {discount}% OFF
                            </Tag>
                          </Space>
                        </Col>
                      </Row>
                    </div>
                  );
                }}
              </Form.Item>

              <Row gutter={16}>
                <Col span={isLifetimeValidity ? 24 : 12}>
                  <Form.Item
                    label={
                      <span style={{ fontWeight: 600, color: "#6b7280" }}>
                        ONE TIME FEE
                      </span>
                    }
                    name="oneTimeFee"
                    rules={[
                      { required: true, message: "Please input one time fee!" },
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    <InputNumber
                      size="large"
                      placeholder="e.g. 25000"
                      min={0}
                      style={{ width: "100%", borderRadius: 8 }}
                      // prefix="₹"
                    />
                  </Form.Item>
                </Col>

                {!isLifetimeValidity && (
                  <Col span={12}>
                    <Form.Item
                      label={
                        <span style={{ fontWeight: 600, color: "#6b7280" }}>
                          SUBSCRIPTION FEE
                        </span>
                      }
                      name="subscriptionFee"
                      rules={[
                        {
                          required: true,
                          message: "Please input subscription fee!",
                        },
                      ]}
                      style={{ marginBottom: 24 }}
                    >
                      <InputNumber
                        size="large"
                        placeholder="e.g. 10000"
                        min={0}
                        style={{ width: "100%", borderRadius: 8 }}
                        // prefix="₹"
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <Space>
                        <span style={{ fontWeight: 600, color: "#6b7280" }}>
                          OFFER EXPIRY DATE
                        </span>
                        <Tooltip title="Select the expiry date for this offer">
                          <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                        </Tooltip>
                      </Space>
                    }
                    name="expiryDate"
                    rules={[
                      {
                        required: true,
                        message: "Please select expiry date!",
                      },
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    <DatePicker
                      size="large"
                      placeholder="Select Expiry Date"
                      style={{ width: "100%", borderRadius: 8 }}
                      format="DD MMM YYYY"
                      disabledDate={(current) =>
                        current && current < moment().startOf("day")
                      }
                      showTime={false}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginBottom: 24, marginTop: 32 }}>
                <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                  <Button
                    size="large"
                    onClick={handleModalCancel}
                    style={{
                      borderRadius: 8,
                      minWidth: 120,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    style={{
                      background: "#7c3aed",
                      borderColor: "#7c3aed",
                      borderRadius: 8,
                      minWidth: 160,
                    }}
                  >
                    {selectedCreator ? "Update Config" : "Save Config"}
                  </Button>
                </Space>
              </Form.Item>
            </>
          )}

          {/* Cancel button when not verified */}
          {!isVerified && (
            <Form.Item style={{ marginBottom: 24, marginTop: 16 }}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button
                  size="large"
                  onClick={handleModalCancel}
                  style={{
                    borderRadius: 8,
                    minWidth: 120,
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Filter Modal */}
      <Modal
        title="Filter Creators"
        open={isFilterModalOpen}
        onCancel={handleFilterModalCancel}
        footer={null}
        width={450}
        destroyOnClose
        centered
      >
        <Form
          form={filterForm}
          layout="vertical"
          onFinish={handleApplyFilters}
          style={{ marginTop: 0 }}
        >
          <Form.Item
            label={
              <span style={{ fontWeight: 600, color: "#6b7280" }}>STATUS</span>
            }
            name="status"
            style={{ marginBottom: 24 }}
          >
            <Select
              mode="multiple"
              size="large"
              placeholder="Select Status"
              style={{ width: "100%", borderRadius: 8 }}
              allowClear
              options={statusOptions}
            />
          </Form.Item>

          <Form.Item
            label={
              <span style={{ fontWeight: 600, color: "#6b7280" }}>PLAN</span>
            }
            name="plan"
            style={{ marginBottom: 24 }}
          >
            <Select
              mode="multiple"
              size="large"
              placeholder="Select Plan"
              style={{ width: "100%", borderRadius: 8 }}
              allowClear
              options={planOptions}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.plan !== currentValues.plan
            }
          >
            {({ getFieldValue }) => {
              const selectedPlan = getFieldValue("plan") || [];
              const isOnlyTagmango =
                selectedPlan.length === 1 && selectedPlan[0] === "tagmango";

              if (isOnlyTagmango) {
                return null;
              }

              return (
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600, color: "#6b7280" }}>
                      TIER
                    </span>
                  }
                  name="tier"
                  style={{ marginBottom: 24 }}
                >
                  <Select
                    mode="multiple"
                    size="large"
                    placeholder="Select Tier"
                    style={{ width: "100%", borderRadius: 8 }}
                    allowClear
                    options={tierOptions}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={handleClearFilters}
                style={{
                  borderRadius: 8,
                  minWidth: 120,
                }}
              >
                Clear All
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{
                  background: "#7c3aed",
                  borderColor: "#7c3aed",
                  borderRadius: 8,
                  minWidth: 120,
                }}
              >
                Apply Filters
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* No Configured Mango Modal */}
      <Modal
        title="Deleted Mango"
        open={showNoMangoModal}
        onCancel={() => setShowNoMangoModal(false)}
        centered
        zIndex={9999999}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setShowNoMangoModal(false)}
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
          >
            Okay
          </Button>,
        ]}
      >
        <Typography.Text>
          The Mango linked to this plan has been deleted. Please configure a
          valid Mango before creating this item.
        </Typography.Text>
      </Modal>
    </>
  );
};

export default CreatorList;
